// firebaseHooks.ts - Simplified and fixed version

import { doc, onSnapshot } from "firebase/firestore";
import { useEffect, useState } from "react";
import { db } from "../firebase";

export interface UseFirebaseMenuResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

export const useFirebaseProductOverview = (
  restaurantId: string,
  date?: Date
): UseFirebaseMenuResult<any[]> => {
  // Changed return type to array directly
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!restaurantId) {
      setLoading(false);
      setData([]);
      return;
    }

    setLoading(true);

    // Format date as YYYYMMDD
    const targetDate = date || new Date();
    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, "0");
    const day = String(targetDate.getDate()).padStart(2, "0");
    const dateString = `${year}${month}${day}`;

    const productOverviewDocRef = doc(
      db,
      "Restaurants",
      restaurantId,
      "inventories",
      dateString,
      "projections",
      "productOverview"
    );

    // console.log("Fetching from path:", productOverviewDocRef.path);

    const unsubscribe = onSnapshot(
      productOverviewDocRef,
      (docSnapshot) => {
        try {
          if (docSnapshot.exists()) {
            const docData = docSnapshot.data();
            // console.log("Raw Firebase data:", docData);

            let productsArray: any[] = [];

            if (docData && docData.products) {
              // Check if products is a map (Firestore format) or already an object
              const productsMap =
                docData.products.mapValue?.fields || docData.products;

              // Convert to array format
              productsArray = Object.keys(productsMap).map((key) => {
                const item = productsMap[key];

                // Handle both Firestore format and regular object format
                const itemData = item.mapValue?.fields || item;

                return {
                  id: itemData.itemId || key,
                  displayName: itemData.displayName || "",
                  itemId: itemData.itemId || key,
                  productId: itemData.productId || "",
                  ProductId: itemData.productId || "", // For compatibility
                  quantities: {
                    allocated: Number(itemData.quantities?.allocated) || 0,
                    produced: Number(itemData.quantities?.produced) || 0,
                    reserved: Number(itemData.quantities?.reserved) || 0,
                    cancelled: Number(itemData.quantities?.cancelled) || 0,
                  },
                };
              });

              // console.log("Products array:", productsArray);
            } else {
              // console.log("No products data found");
            }

            setData(productsArray);
          } else {
            // console.log("Document does not exist");
            setData([]);
          }
          setLoading(false);
          setError(null);
        } catch (err) {
          console.error("Error parsing document data:", err);
          setError(err as Error);
          setLoading(false);
          setData([]);
        }
      },
      (err) => {
        console.error("Error fetching product overview document:", err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, date?.getTime()]);

  return { data, loading, error };
};
